import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api, ApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { canOverrideEviction } from "../lib/config";
import { TENANT_STATUSES, tenantStatusLabel } from "../lib/tenants";
import { formatDate } from "../lib/workorders";
import type { EvictionMatch, TenantCreate } from "../api/types";

export function NewTenantPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [buildingId, setBuildingId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [emergencyName, setEmergencyName] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");
  const [status, setStatus] = useState("active");
  const [moveInDate, setMoveInDate] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<EvictionMatch | null>(null);
  const [nameWarning, setNameWarning] = useState<EvictionMatch | null>(null);
  const [overrideReason, setOverrideReason] = useState("");

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });
  const unitsQ = useQuery({
    queryKey: ["ref", "units", buildingId],
    queryFn: () => api.reference.units(Number(buildingId)),
    enabled: Number.isInteger(Number(buildingId)) && buildingId !== "",
  });

  type SubmitOpts = { override?: boolean; acknowledgeNameMatch?: boolean };

  function buildBody(opts: SubmitOpts): TenantCreate {
    return {
      buildingId: Number(buildingId),
      unitId: unitId ? Number(unitId) : null,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth,
      phone: phone.trim() || null,
      email: email.trim() || null,
      emergencyContactName: emergencyName.trim() || null,
      emergencyContactPhone: emergencyPhone.trim() || null,
      status,
      moveInDate: moveInDate || null,
      notes: notes.trim() || null,
      ...(opts.override ? { override: true, overrideReason: overrideReason.trim() } : {}),
      ...(opts.acknowledgeNameMatch ? { acknowledgeNameMatch: true } : {}),
    };
  }

  const createMut = useMutation({
    mutationFn: (opts: SubmitOpts) => api.tenants.create(buildBody(opts)),
    onSuccess: ({ id }) => navigate(`/tenants/${id}`),
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        err.body &&
        typeof err.body === "object"
      ) {
        const body = err.body as { error?: string; match?: EvictionMatch };
        if (body.error === "eviction_match" && body.match) {
          setBlocked(body.match);
          return;
        }
        if (body.error === "eviction_name_warning" && body.match) {
          setNameWarning(body.match);
          return;
        }
      }
      setError("Could not save the tenant. Please try again.");
    },
  });

  function onSave() {
    setError(null);
    if (!buildingId) return setError("Please choose a building.");
    if (!firstName.trim() || !lastName.trim()) {
      return setError("Please enter the tenant's first and last name.");
    }
    if (!dateOfBirth) {
      return setError("Date of birth is required — it is the eviction-list match key.");
    }
    createMut.mutate({});
  }

  if (nameWarning) {
    return (
      <Layout title="Add a Tenant">
        <section className="card">
          <div className="warn-box">
            <strong>&#9888; Possible eviction-list match — please check</strong>
            Someone with this exact name is on the eviction list, but the date of birth is
            different.
          </div>
          <dl className="detail-list">
            <div>
              <dt>Name on eviction list</dt>
              <dd>
                {nameWarning.firstName} {nameWarning.lastName}
              </dd>
            </div>
            <div>
              <dt>Date of birth on file</dt>
              <dd>{formatDate(nameWarning.dateOfBirth)}</dd>
            </div>
            <div>
              <dt>Date of birth you entered</dt>
              <dd>{formatDate(dateOfBirth)}</dd>
            </div>
            <div>
              <dt>Evicted</dt>
              <dd>{formatDate(nameWarning.evictionDate)}</dd>
            </div>
            <div>
              <dt>Recorded at</dt>
              <dd>{nameWarning.originBuildingName ?? "—"}</dd>
            </div>
            <div>
              <dt>Reason</dt>
              <dd>{nameWarning.reason ?? "—"}</dd>
            </div>
          </dl>
          <p className="help-line">
            If the date of birth might be mistyped, go back and correct it — this could be the same
            person. If you are sure this is a <strong>different person</strong>, you can create the
            tenant.
          </p>
          {createMut.isError && <p className="field-error">Could not create. Please try again.</p>}
          <div className="form-actions">
            <button
              className="btn btn-ghost btn-xl"
              onClick={() => setNameWarning(null)}
              disabled={createMut.isPending}
            >
              Go back and check
            </button>
            <button
              className="btn btn-primary btn-xl"
              onClick={() => createMut.mutate({ acknowledgeNameMatch: true })}
              disabled={createMut.isPending}
            >
              {createMut.isPending ? "Creating…" : "Different person — create anyway"}
            </button>
          </div>
        </section>
      </Layout>
    );
  }

  if (blocked) {
    return (
      <Layout title="Add a Tenant">
        <section className="card">
          <div className="block-banner">
            <div className="block-ico" aria-hidden="true">
              &#9888;
            </div>
            <div>
              <h2>This tenant cannot be added</h2>
              <p>This person is on the eviction list.</p>
            </div>
          </div>
          <dl className="detail-list">
            <div>
              <dt>Name</dt>
              <dd>
                {blocked.firstName} {blocked.lastName}
              </dd>
            </div>
            <div>
              <dt>Date of birth</dt>
              <dd>{formatDate(blocked.dateOfBirth)}</dd>
            </div>
            <div>
              <dt>Evicted</dt>
              <dd>{formatDate(blocked.evictionDate)}</dd>
            </div>
            <div>
              <dt>Recorded at</dt>
              <dd>{blocked.originBuildingName ?? "—"}</dd>
            </div>
            <div>
              <dt>Reason</dt>
              <dd>{blocked.reason ?? "—"}</dd>
            </div>
            <div>
              <dt>Match on</dt>
              <dd>Name + date of birth</dd>
            </div>
          </dl>
          <p className="help-line">
            A new tenant record was <strong>not</strong> created. If you believe this is a
            different person, only <strong>Management</strong> can review and override.
          </p>
          {user && canOverrideEviction(user.role) ? (
            <>
              <label className="field">
                <span>Override reason (recorded on the tenant)</span>
                <textarea
                  rows={2}
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Explain why this is a genuinely different person…"
                />
              </label>
              {createMut.isError && (
                <p className="field-error">Could not override. Please try again.</p>
              )}
              <div className="form-actions">
                <button
                  className="btn btn-ghost btn-xl"
                  onClick={() => setBlocked(null)}
                  disabled={createMut.isPending}
                >
                  Go back
                </button>
                <button
                  className="btn btn-primary btn-xl"
                  onClick={() => createMut.mutate({ override: true })}
                  disabled={createMut.isPending || !overrideReason.trim()}
                >
                  {createMut.isPending ? "Creating…" : "Override and create tenant"}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="sub" style={{ textAlign: "right" }}>
                Management only · Staff cannot override this match.
              </p>
              <div className="form-actions">
                <button className="btn btn-primary btn-xl" onClick={() => setBlocked(null)}>
                  Go back
                </button>
              </div>
            </>
          )}
        </section>
      </Layout>
    );
  }

  return (
    <Layout title="Add a Tenant">
      <p className="help-line">
        Pick the building first. <strong>Date of birth is required</strong> — it is used to check
        the eviction list when the tenant is saved.
      </p>
      <section className="card form-card">
        <label className="field">
          <span>Building</span>
          <select
            value={buildingId}
            onChange={(e) => {
              setBuildingId(e.target.value);
              setUnitId("");
            }}
          >
            <option value="">Choose a building…</option>
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Unit</span>
          <select
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            disabled={!buildingId || unitsQ.isLoading}
          >
            <option value="">No unit assigned</option>
            {(unitsQ.data?.units ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                Unit {u.unitNumber}
                {u.floor ? `, floor ${u.floor}` : ""}
              </option>
            ))}
          </select>
        </label>

        <div className="two-col">
          <label className="field">
            <span>First name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </label>
          <label className="field">
            <span>Last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Date of birth</span>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {TENANT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {tenantStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Phone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
          </label>
          <label className="field">
            <span>Email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Emergency contact name</span>
            <input value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} />
          </label>
          <label className="field">
            <span>Emergency contact phone</span>
            <input
              value={emergencyPhone}
              onChange={(e) => setEmergencyPhone(e.target.value)}
              inputMode="tel"
            />
          </label>
        </div>

        <label className="field">
          <span>Move-in date</span>
          <input type="date" value={moveInDate} onChange={(e) => setMoveInDate(e.target.value)} />
        </label>

        <label className="field">
          <span>Notes</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        {error && <p className="field-error">{error}</p>}

        <div className="form-actions">
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/tenants")}
            disabled={createMut.isPending}
          >
            Cancel
          </button>
          <button className="btn btn-primary btn-xl" onClick={onSave} disabled={createMut.isPending}>
            {createMut.isPending ? "Saving…" : "Save tenant"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
